// @ts-nocheck
/**
 * TOMI — indicador de "mais imagens" + barrinha de rolagem minimalista para
 * as thumbnails da galeria de mídia do produto (Product media > Thumbnails).
 * Totalmente aditivo: só age em containers com a classe .tomi-thumbs-scroll
 * (adicionada em snippets/slideshow-controls.liquid), sem tocar no
 * comportamento nativo do carrossel.
 */

/**
 * @param {HTMLElement} container
 */
function updateThumbScrollState(container) {
  const { scrollLeft, scrollWidth, clientWidth } = container;
  const hasOverflow = scrollWidth > clientWidth + 1;
  const atEnd = scrollLeft >= scrollWidth - clientWidth - 1;
  const atStart = scrollLeft <= 1;

  container.classList.toggle('tomi-thumbs--has-more', hasOverflow && !atEnd);
  container.classList.toggle('tomi-thumbs--has-prev', hasOverflow && !atStart);

  const controls = container.closest('slideshow-controls');
  if (!controls) return;

  const progress = hasOverflow ? scrollLeft / (scrollWidth - clientWidth) : 0;
  const visibleRatio = hasOverflow ? clientWidth / scrollWidth : 1;

  controls.style.setProperty('--tomi-thumb-progress', String(progress));
  controls.style.setProperty('--tomi-thumb-visible-ratio', String(Math.min(1, visibleRatio)));

  const badge = controls.querySelector('[data-tomi-thumb-remaining]');
  if (badge) {
    const items = container.querySelectorAll('.slideshow-controls__thumbnail');
    const totalCount = items.length;
    const avgItemWidth = totalCount > 0 ? scrollWidth / totalCount : 0;
    const visibleCount = avgItemWidth > 0 ? Math.round(clientWidth / avgItemWidth) : totalCount;
    const remaining = Math.max(0, totalCount - visibleCount);
    badge.textContent = `+${remaining}`;
  }
}

/**
 * @param {HTMLElement} container
 */
function initThumbContainer(container) {
  updateThumbScrollState(container);
  container.addEventListener('scroll', () => updateThumbScrollState(container), { passive: true });
  new ResizeObserver(() => updateThumbScrollState(container)).observe(container);

  const controls = container.closest('slideshow-controls');
  const nudgeButtons = controls?.querySelectorAll('.tomi-thumb-nudge') || [];

  nudgeButtons.forEach((nudgeButton) => {
    const direction = nudgeButton.dataset.tomiThumbDirection === 'prev' ? -1 : 1;
    nudgeButton.addEventListener('click', () => {
      container.scrollBy({ left: container.clientWidth * 0.8 * direction, behavior: 'smooth' });
    });
  });

  bindThumbDragScroll(container);
}

/**
 * TOMI NOVO — arrastar com o mouse (desktop) em qualquer ponto da faixa de
 * thumbnails (inclusive em cima de uma thumbnail) rola a lista, sem precisar
 * acertar o "vão" entre elas. Toque (mobile) já rola nativamente via
 * overflow-x, então aqui só tratamos ponteiro tipo mouse/pen. Um clique
 * "de verdade" (sem arrastar além do limiar) continua selecionando a
 * thumbnail normalmente — só suprimimos o click sintético quando o usuário
 * realmente arrastou, usando um listener de captura em `window` (que roda
 * ANTES do listener de delegação global em `document` de assets/component.js,
 * já que a fase de captura desce de window -> document -> ... -> alvo).
 * @param {HTMLElement} container
 */
function bindThumbDragScroll(container) {
  const DRAG_THRESHOLD = 6;
  let dragging = false;
  let didDrag = false;
  let startX = 0;
  let startScrollLeft = 0;
  let pointerId = null;

  container.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'touch') return;
    dragging = true;
    didDrag = false;
    pointerId = event.pointerId;
    startX = event.clientX;
    startScrollLeft = container.scrollLeft;
    // TOMI: pointer capture só é tomado depois que o arrasto é confirmado no
    // pointermove abaixo — capturar aqui, no pointerdown, faz o navegador
    // redirecionar o "click" de um toque simples (ex: selecionar a
    // thumbnail) pro container em vez do botão clicado, quebrando o clique.
  });

  container.addEventListener('pointermove', (event) => {
    if (!dragging || event.pointerId !== pointerId) return;

    // TOMI: auto-recuperação — se um pointerup/pointercancel anterior não
    // resetou o estado (ex: botão solto fora da janela), um pointermove sem
    // o botão principal pressionado encerra o arrasto na hora, em vez de
    // ficar "preso" respondendo a simples hover do mouse (bug relatado:
    // thumbnails "arrastando sozinhas" só ao passar o mouse por cima).
    if (!(event.buttons & 1)) {
      dragging = false;
      container.removeAttribute('data-tomi-dragging');
      return;
    }

    const delta = event.clientX - startX;
    if (!didDrag && Math.abs(delta) > DRAG_THRESHOLD) {
      didDrag = true;
      container.setPointerCapture(event.pointerId);
      container.setAttribute('data-tomi-dragging', 'true');
    }
    if (didDrag) {
      container.scrollLeft = startScrollLeft - delta;
      event.preventDefault();
    }
  });

  const endDrag = (event) => {
    if (!dragging || event.pointerId !== pointerId) return;
    dragging = false;
    container.removeAttribute('data-tomi-dragging');
    if (didDrag) tomiSuppressNextThumbClick = true;
  };

  container.addEventListener('pointerup', endDrag);
  container.addEventListener('pointercancel', endDrag);
}

/** @type {boolean} */
let tomiSuppressNextThumbClick = false;

window.addEventListener(
  'click',
  (event) => {
    if (!tomiSuppressNextThumbClick) return;
    tomiSuppressNextThumbClick = false;
    event.stopImmediatePropagation();
    event.preventDefault();
  },
  { capture: true }
);

function scanForThumbContainers(root = document) {
  root.querySelectorAll('.tomi-thumbs-scroll').forEach((container) => {
    if (container.dataset.tomiThumbInit) return;
    container.dataset.tomiThumbInit = 'true';
    initThumbContainer(container);
  });
}

scanForThumbContainers();

const tomiThumbObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.addedNodes.length) {
      scanForThumbContainers();
      break;
    }
  }
});

tomiThumbObserver.observe(document.body, { childList: true, subtree: true });
